/* @jsx jsx */
import { useButton } from '@react-aria/button';
import { HiddenSelect, useSelect } from '@react-aria/select';
import { useSelectState } from '@react-stately/select';
import { AriaSelectProps } from '@react-types/select';
import { jsx } from '@theme-ui/core';
import * as React from 'react';
import { ListBox } from './Listbox';
import { Popover } from './Popover';
import { FilterButton } from './FilterButton';
import { Icon } from './Icon2';
import { AriaPositionProps, OverlayContainer, useOverlayPosition } from '@react-aria/overlays';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { useOverlayTrigger } from '@react-aria/overlays';
import { useTranslation } from '@front10/utils';

export interface SelectProps<T> extends AriaSelectProps<T> {
  overlayPosition?: Omit<AriaPositionProps, 'onClose' | 'overlayRef' | 'targetRef'>;
  hideLabel?: boolean;
}

function SelectCmp<T extends object>(
  { overlayPosition, hideLabel = false, ...props }: SelectProps<T>,
  ref: any
) {
  // Create state based on the incoming props
  let state = useSelectState(props);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const translate = useTranslation();

  // Get props for child elements from useSelect
  let ref_ = React.useRef<any>(null);
  let { triggerProps, menuProps, valueProps, labelProps } = useSelect(props, state, ref_);

  // Get props for the button based on the trigger props from useSelect
  let { buttonProps } = useButton(triggerProps, ref_);

  let overlayState = useOverlayTriggerState({
    isOpen: state.isOpen
  });

  // Get props for the trigger and overlay. This also handles
  // hiding the overlay when a parent element of the trigger scrolls
  // (which invalidates the popover positioning).
  let { triggerProps: triggerProps_ } = useOverlayTrigger({ type: 'dialog' }, overlayState, ref_);

  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps } = useOverlayPosition({
    targetRef: ref_,
    overlayRef,
    placement: 'bottom start',
    offset: 0,
    isOpen: state.isOpen,
    ...overlayPosition
  });

  React.useImperativeHandle(
    ref,
    () => ({
      selectOption: (optionKey: any) => {
        state.selectionManager.setSelectedKeys([optionKey]);
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={ref}>
      <VisuallyHidden {...labelProps}>{props.label}</VisuallyHidden>
      <HiddenSelect state={state} triggerRef={ref_} label={props.label} name={props.name} />
      <FilterButton {...buttonProps} ref={ref_}>
        <Icon aria-hidden="true" icon="sort" size={20} />
        {!hideLabel && (
          <span {...valueProps}>
            {state.selectedItem
              ? state.selectedItem.rendered
              : translate('cmp-filter-select-an-option-label', 'Select an option')}
          </span>
        )}
        <Icon aria-hidden="true" icon="keyboard_arrow_down" size={20} />
      </FilterButton>
      {state.isOpen && (
        <OverlayContainer>
          <Popover
            {...positionProps}
            {...triggerProps_}
            isOpen={state.isOpen}
            onClose={() => {
              if (ref_ && ref_?.current) {
                ref_?.current.focus();
              }
              state.close();
            }}
            popoverRef={overlayRef}
          >
            <ListBox {...menuProps} state={state} />
          </Popover>
        </OverlayContainer>
      )}
    </div>
  );
}

export const Select = React.forwardRef(SelectCmp);
